import { useLanguage } from '../../../contexts/LanguageContext';
import { LinkedText } from '../../../components/LinkedText';
import RegisterLink from '../../../components/RegisterLink';
import heroGameTipsBanner from '../../../assets/hero-game-tips-banner.png';
import heroBadgeSsl from '../../../assets/hero-badge-ssl.png';
import heroBadgeMoney from '../../../assets/hero-badge-money.png';
import heroBadgeLicensed from '../../../assets/hero-badge-licensed.png';
import heroMyrMalaysiaFlag from '../../../assets/hero-myr-malaysia-flag.png';

export default function HeroSection() {
  const { t } = useLanguage();

  const features = [
    { icon: 'ri-shield-check-line', en: '100% Secure', bm: '100% Selamat' },
    { icon: 'ri-flashlight-line', en: 'Instant Withdrawals', bm: 'Pengeluaran Segera' },
    { icon: 'ri-gift-2-line', en: 'Big Welcome Bonus', bm: 'Bonus Selamat Datang' },
    { icon: 'ri-customer-service-2-line', en: '24/7 Support', bm: 'Sokongan 24/7' },
    { icon: 'ri-global-line', en: 'Multi-Language', bm: 'Bahasa Multi' },
  ];

  const stats = [
    { value: '400K+', en: 'Active Members', bm: 'Ahli Aktif' },
    { value: 'RM4M+', en: 'Total Prizes', bm: 'Hadiah Dibayar' },
    { value: '4000+', en: 'Games Available', bm: 'Permainan' },
    { value: '98.9%', en: 'Uptime', bm: 'Masa Aktif' },
  ];

  const badges = [
    { image: heroBadgeSsl, en: 'SSL Secured', bm: 'SSL Selamat' },
    { image: heroBadgeMoney, en: 'Fast Payout', bm: 'Bayaran Cepat' },
    { image: heroBadgeLicensed, en: 'Licensed', bm: 'Berlesen' },
  ];

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0d0000 0%, #1a0303 40%, #200505 70%, #0d0000 100%)',
      }}
    >
      {/* Radial glow effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 md:w-96 h-64 md:h-96 bg-[#cc1100]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-56 md:w-80 h-56 md:h-80 bg-[#d4a017]/8 rounded-full blur-3xl" />
        <div className="absolute top-0 right-0 w-48 md:w-64 h-48 md:h-64 bg-[#cc1100]/8 rounded-full blur-2xl" />
      </div>

      {/* Diamond grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'repeating-linear-gradient(45deg, #d4a017 0, #d4a017 1px, transparent 0, transparent 50%)',
          backgroundSize: '24px 24px',
        }}
      />

      {/* Top red border line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#cc1100] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 w-full py-24 md:py-32 grid lg:grid-cols-2 gap-10 items-center">
        {/* Left Content */}
        <div>
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-[#cc1100]/20 border border-[#cc1100]/40 rounded-full px-4 py-2 mb-5">
            <span className="w-2 h-2 bg-[#d4a017] rounded-full animate-pulse" />
            <span className="text-[#d4a017] text-xs md:text-sm font-semibold tracking-wide">
              {t("Malaysia's 🥇 Trusted Online Casino", "Kasino Dalam Talian 🥇 Malaysia")}
            </span>
          </div>

          {/* Title */}
          <h1 className="mb-4">
            <span className="block text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight">
              {t('Welcome to', 'Selamat Datang ke')}{' '}
              <span
                className="text-transparent bg-clip-text"
                style={{ backgroundImage: 'linear-gradient(135deg, #d4a017, #f5c518, #d4a017)' }}
              >
                MrCuci
              </span>
            </span>
            <span className="block text-lg md:text-xl lg:text-2xl font-medium text-red-300/80 mt-3">
              {t('Your Premier Online Gaming Destination', 'Destinasi Perjudian Dalam Talian Utama Anda')}
            </span>
          </h1>

          {/* Description */}
          <p className="text-gray-300/80 text-sm md:text-base leading-relaxed mb-3 max-w-lg">
            <LinkedText>{t(
              'Experience world-class gaming with lightning-fast withdrawals, generous casino bonuses, and thousands of online slot games, live dealer tables, and fishing arcade options.',
              'Nikmati permainan bertaraf dunia dengan pengeluaran yang pantas, bonus kasino yang lumayan, dan ribuan permainan slot dalam talian, meja peniaga langsung, dan pilihan arked memancing.'
            )}</LinkedText>
          </p>
          <p className="text-gray-400 text-xs md:text-sm leading-relaxed mb-6 max-w-lg">
            <LinkedText>{t(
              'MrCuci is trusted by hundreds of thousands of Malaysian players, offering unmatched security, transparent odds, and the biggest jackpots in Malaysia. Join today and claim your exclusive welcome package.',
              'MrCuci dipercayai oleh ratusan ribu pemain Malaysia, menawarkan keselamatan terbaik, peluang yang telus, dan jackpot terbesar di Malaysia. Daftar hari ini dan tuntut pakej selamat datang eksklusif anda.'
            )}</LinkedText>
          </p>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-2 mb-6">
            {features.map((item) => (
              <div
                key={item.en}
                className="flex items-center gap-2 bg-[#1a0303]/80 border border-[#d4a017]/20 rounded-full px-3 md:px-4 py-2 hover:border-[#d4a017]/50 transition-colors"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-[#d4a017] text-sm`} />
                </span>
                <span className="text-gray-200 text-xs font-medium whitespace-nowrap">{t(item.en, item.bm)}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-3 mb-8">
            <RegisterLink
              source="hero_join_now"
              label={t('Join MrCuci Now', 'Sertai MrCuci Sekarang')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#cc1100] to-[#ff2200] text-white font-bold px-6 md:px-8 py-3 md:py-3.5 rounded-lg hover:opacity-90 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm shadow-[0_4px_20px_rgba(204,17,0,0.4)]"
            >
              <span>{t('Join MrCuci Now', 'Sertai MrCuci Sekarang')}</span>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-base" />
              </span>
            </RegisterLink>
            <RegisterLink
              source="hero_claim_bonus"
              label={t('Claim Bonus', 'Tuntut Bonus')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#d4a017] to-[#f5c518] text-black font-bold px-6 md:px-8 py-3 md:py-3.5 rounded-lg hover:opacity-90 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm shadow-[0_4px_16px_rgba(212,160,23,0.35)]"
            >
              <span>{t('Claim Bonus', 'Tuntut Bonus')}</span>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-gift-2-line text-base" />
              </span>
            </RegisterLink>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6 border-t border-[#d4a017]/15">
            {stats.map((stat) => (
              <div key={stat.en} className="text-center">
                <div
                  className="text-xl md:text-2xl font-black text-transparent bg-clip-text"
                  style={{ backgroundImage: 'linear-gradient(135deg, #d4a017, #f5c518)' }}
                >
                  {stat.value}
                </div>
                <div className="text-gray-500 text-xs mt-1 whitespace-nowrap">{t(stat.en, stat.bm)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — Promotional banner */}
        <div className="flex flex-col items-center gap-6 mt-4 lg:mt-0">
          <div className="relative w-full max-w-md md:max-w-lg xl:max-w-2xl">
            <div className="absolute inset-0 rounded-2xl bg-[#cc1100]/20 blur-3xl scale-[0.92] animate-pulse" />
            <div className="absolute inset-0 rounded-2xl bg-[#d4a017]/12 blur-2xl scale-[0.88]" />
            <img
              src={heroGameTipsBanner}
              alt={t('Game tips and bet ratio', 'Petua permainan dan nisbah pertaruhan')}
              className="relative w-full h-auto object-contain drop-shadow-2xl rounded-xl"
            />
          </div>

          {/* MYR Banner */}
          <div
            className="w-full max-w-xs rounded-xl px-4 py-3 flex items-center justify-center gap-3"
            style={{ background: 'linear-gradient(135deg, #1a0303, #2a0606)', border: '1px solid rgba(212,160,23,0.3)' }}
          >
            <img
              src={heroMyrMalaysiaFlag}
              alt={t('Malaysia flag', 'Bendera Malaysia')}
              className="h-9 w-auto max-w-[3.25rem] shrink-0 rounded-md object-contain"
            />
            <div>
              <p className="text-white font-black text-sm">{t('Malaysian Ringgit (MYR)', 'Ringgit Malaysia (MYR)')}</p>
              <p className="text-gray-400 text-xs">{t('Deposit & Withdraw in Ringgit', 'Deposit & Keluarkan dalam Ringgit')}</p>
            </div>
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-3 gap-3 w-full max-w-xs">
            {badges.map((badge) => (
              <div
                key={badge.en}
                className="flex flex-col items-center gap-2 bg-[#1a0303]/60 border border-[#d4a017]/20 rounded-xl py-3 px-2"
              >
                <img src={badge.image} alt="" className="w-10 h-10 object-contain" />
                <span className="text-gray-400 text-xs font-medium text-center leading-tight">{t(badge.en, badge.bm)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#080000] to-transparent pointer-events-none" />
    </section>
  );
}
